import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import { BaseResponse } from "../../../shared/types";
import {
  BannerImagesResponse,
  BannerImageUploadResponse,
  BannerResponse,
  BannersResponse,
  ClickActionsResponse,
  CreateBannerRequest,
  CreateTargetingRuleRequest,
  GetBannersParams,
  TargetingRuleResponse,
  TargetingRulesResponse,
  UpdateBannerRequest,
  UpdateTargetingRuleRequest,
} from "./types";

export const adminBannerApi = createApi({
  reducerPath: "adminBannerApi",
  baseQuery: baseApiQuery,
  tagTypes: ["Banners", "BannerImages", "ClickActions", "TargetingRules"],
  endpoints: (builder) => ({
    getBanners: builder.query<BannersResponse, GetBannersParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();

        if (params.search) queryParams.append("search", params.search);
        if (params.display !== undefined)
          queryParams.append("display", params.display.toString());
        if (params.sort) queryParams.append("sort", params.sort);
        if (params.order) queryParams.append("order", params.order);
        if (params.pageToken) queryParams.append("pageToken", params.pageToken);
        if (params.limit !== undefined)
          queryParams.append("limit", params.limit.toString());

        return `/admin/banners${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      },
      providesTags: ["Banners"],
    }),

    createBanner: builder.mutation<BaseResponse, CreateBannerRequest>({
      query: (body) => ({
        url: "/admin/banners",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Banners"],
    }),

    updateBanner: builder.mutation<
      BannerResponse,
      { id: number; body: UpdateBannerRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/banners/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Banners"],
    }),

    deleteBanner: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/banners/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Banners"],
    }),

    getBannerImages: builder.query<BannerImagesResponse, number>({
      query: (id) => `/admin/banners/${id}/images`,
      providesTags: (_result, _error, id) => [{ type: "BannerImages", id }],
    }),

    uploadBannerImage: builder.mutation<
      BannerImageUploadResponse,
      { id: number; file: FormData }
    >({
      query: ({ id, file }) => ({
        url: `/admin/banners/${id}/images`,
        method: "POST",
        body: file,
        headers: {
          // Remove the explicit Content-Type to let the browser set it with boundary
        },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "BannerImages", id },
        "Banners",
      ],
    }),

    deleteBannerImage: builder.mutation<
      BaseResponse,
      { id: number; imageId: number }
    >({
      query: ({ id, imageId }) => ({
        url: `/admin/banners/${id}/images/${imageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "BannerImages", id },
        "Banners",
      ],
    }),

    // New endpoints for click actions
    getClickActions: builder.query<ClickActionsResponse, void>({
      query: () => `/admin/banners/click/actions`,
      providesTags: ["ClickActions"],
    }),

    // New endpoints for targeting rules
    getBannerTargetingRules: builder.query<TargetingRulesResponse, number>({
      query: (id) => `/admin/banners/${id}/targeting/rules`,
      providesTags: (_result, _error, id) => [{ type: "TargetingRules", id }],
    }),

    createTargetingRule: builder.mutation<
      TargetingRuleResponse,
      { id: number; body: CreateTargetingRuleRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/banners/${id}/targeting/rules`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "TargetingRules", id },
      ],
    }),

    updateTargetingRule: builder.mutation<
      BaseResponse,
      { id: number; ruleId: number; body: UpdateTargetingRuleRequest }
    >({
      query: ({ id, ruleId, body }) => ({
        url: `/admin/banners/${id}/targeting/rules/${ruleId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "TargetingRules", id },
      ],
    }),

    deleteTargetingRule: builder.mutation<
      BaseResponse,
      { id: number; ruleId: number }
    >({
      query: ({ id, ruleId }) => ({
        url: `/admin/banners/${id}/targeting/rules/${ruleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "TargetingRules", id },
      ],
    }),
  }),
});

export const {
  useGetBannersQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
  useGetBannerImagesQuery,
  useUploadBannerImageMutation,
  useDeleteBannerImageMutation,
  useGetClickActionsQuery,
  useGetBannerTargetingRulesQuery,
  useCreateTargetingRuleMutation,
  useUpdateTargetingRuleMutation,
  useDeleteTargetingRuleMutation,
} = adminBannerApi;
